import { useEffect, useState } from "react";
import type { TickerItem } from "../../../shared/models";
import { useTicker } from "../../data/queries";
import { Icon, type IconName } from "../../ui/Icon";
import { Sheet } from "../../ui/Sheet";
import type { WidgetDefinition, WidgetProps } from "../types";
import "./ticker.css";

interface TickerConfig {
  seconds: number;
}

const ICONS: Record<TickerItem["kind"], IconName> = {
  news: "info",
  onThisDay: "calendar",
  quote: "sparkle",
  joke: "sun",
  word: "edit",
  fact: "sparkle",
};

/**
 * One slim line that quietly rotates through today's headline, quote, joke,
 * word and fact — fun without spending dashboard space. Tap for everything.
 */
function Ticker({ config, editing }: WidgetProps<TickerConfig>) {
  const q = useTicker();
  const items = q.data?.items ?? [];
  const [i, setI] = useState(0);
  const [reveal, setReveal] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (items.length < 2 || open || editing) return;
    const t = window.setTimeout(() => {
      setReveal(false);
      setI((n) => (n + 1) % items.length);
    }, config.seconds * 1000);
    return () => clearTimeout(t);
  }, [i, items.length, config.seconds, open, editing]);

  // Jokes show their punchline halfway through.
  const item = items[i % Math.max(1, items.length)];
  useEffect(() => {
    if (item?.kind !== "joke") return;
    const t = window.setTimeout(() => setReveal(true), (config.seconds * 1000) / 2);
    return () => clearTimeout(t);
  }, [item, config.seconds]);

  if (!item) return <div className="ticker ticker-empty" />;

  return (
    <>
      <button className="ticker" onClick={() => setOpen(true)} aria-label="Today’s headlines and fun facts">
        <span className="ticker-icon">
          <Icon name={ICONS[item.kind]} size="1.05rem" />
        </span>
        <span key={i} className="ticker-body">
          <span className="ticker-label">
            {item.label}
            {item.source && <span className="ticker-source"> · {item.source}</span>}
          </span>
          <span className="ticker-text">
            {item.text}
            {item.detail && (item.kind !== "joke" || reveal) && <span className="ticker-detail"> {item.kind === "quote" ? `— ${item.detail}` : item.detail}</span>}
          </span>
        </span>
        <span className="ticker-dots" aria-hidden="true">
          {items.map((_, n) => (
            <i key={n} className={n === i % items.length ? "on" : ""} />
          ))}
        </span>
      </button>
      {open && (
        <Sheet title="Today" onClose={() => setOpen(false)}>
          <ul className="ticker-list">
            {items.map((it, n) => (
              <li key={n}>
                <span className="ticker-label">
                  <Icon name={ICONS[it.kind]} size="1rem" /> {it.label}
                  {it.source && <span className="ticker-source"> · {it.source}</span>}
                </span>
                <span className="ticker-text">{it.text}</span>
                {it.detail && <span className="muted">{it.kind === "quote" ? `— ${it.detail}` : it.detail}</span>}
              </li>
            ))}
          </ul>
        </Sheet>
      )}
    </>
  );
}

export const tickerWidget: WidgetDefinition<TickerConfig> = {
  type: "ticker",
  version: 1,
  name: "Today’s ticker",
  description: "A slim rotating line: news headline, quote, joke, word of the day, fun fact, on this day.",
  icon: "sparkle",
  category: "time",
  defaultSize: { w: 12, h: 1 },
  minSize: { w: 4, h: 1 },
  maxSize: { w: 12, h: 2 },
  stackHeight: 84,
  defaults: { seconds: 12 },
  settings: [{ key: "seconds", label: "Seconds per item", type: "number", min: 6, max: 60, step: 2 }],
  provider: "feeds",
  chrome: "bare",
  Component: Ticker,
};
